<script lang="ts">
	/**
	 * One queued message, as it was — or will be — sent (§11).
	 *
	 * What support opens when a customer says "I never got that" or "what did that email say". The
	 * bodies are the copy rendered **at enqueue** and stored on the row, so this is exactly what went
	 * out, never a re-render against today's template.
	 *
	 * The HTML is drawn in a **sandboxed** `iframe` with no permissions at all: it is template output
	 * with customer data substituted into it, and an admin screen is the last place to run anything
	 * that arrived in a name field. The sandbox also keeps the email's own stylesheet from restyling
	 * the page around it, which is the commoner way rendering mail inline goes wrong.
	 */

	import { Badge, Modal } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import { EmptyState, ModalHeader, RelativeDate } from '../../../components';
	import { getEmailMessage } from '../../../AppNetwork';
	import type { EmailMessageDetail } from '../../../AppTypes';

	/** The message to show, or empty for none. The parent owns this; see `FormModal`'s own note. */
	export let messageUUID: string = '';

	const dispatcher = createEventDispatcher<{ close: void }>();

	let detail: EmailMessageDetail | null = null;
	let error = '';
	let view: 'html' | 'text' = 'html';

	// A mirror of the parent's prop, for the reason `FormModal` documents.
	let dialogOpen = false;
	$: dialogOpen = messageUUID.length > 0;
	$: if (!dialogOpen && messageUUID.length > 0) dispatcher('close');

	$: void fnLoad(messageUUID);

	async function fnLoad(uuid: string): Promise<void> {
		detail = null;
		error = '';
		view = 'html';
		if (uuid.length === 0) return;
		try {
			const loaded = await getEmailMessage(uuid);
			// A reply for a message the admin has already moved off is dropped, not drawn.
			if (uuid === messageUUID) detail = loaded;
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'The message could not be loaded';
		}
	}
</script>

<Modal bind:open={dialogOpen} size="lg" outsideclose>
	<svelte:fragment slot="header">
		<ModalHeader
			title={detail?.subject ?? 'Email'}
			faIcon="envelope-open-text"
			subtitle={detail === null ? '' : `To ${detail.to} · ${detail.templateKey}`}
		/>
	</svelte:fragment>

	{#if error.length > 0}
		<EmptyState variant="error" title="This message could not be loaded." description={error} />
	{:else if detail === null}
		<EmptyState variant="loading" title="Loading the message…" />
	{:else}
		<div class="space-y-4">
			<dl class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
				<div>
					<dt class="card-label">Status</dt>
					<dd>{detail.status}</dd>
				</div>
				<div>
					<dt class="card-label">Queued</dt>
					<dd><RelativeDate date={detail.creationDate} /></dd>
				</div>
				<div>
					<dt class="card-label">Sent</dt>
					<dd>
						{#if detail.sentAt}<RelativeDate date={detail.sentAt} />{:else}Not yet{/if}
					</dd>
				</div>
				<div>
					<dt class="card-label">Attempts</dt>
					<dd>{detail.attemptCount}</dd>
				</div>
			</dl>

			{#if detail.lastError}
				<p class="text-sm text-red-700 dark:text-red-400">Last error: {detail.lastError}</p>
			{/if}

			{#if detail.attachments.length > 0}
				<p class="text-sm text-default">
					<i class="fa-solid fa-paperclip mr-1" aria-hidden="true" />
					{detail.attachments.length === 1 ? 'One attachment' : `${detail.attachments.length} attachments`},
					rendered when the message is sent:
					{#each detail.attachments as attachment, index (index)}
						<Badge color="dark" class="ml-1">{attachment.kind} {attachment.invoiceUUID}</Badge>
					{/each}
				</p>
			{/if}

			<div class="inline-flex rounded-full bg-depth p-1 text-sm" role="tablist" aria-label="Which body to show">
				<button
					type="button"
					role="tab"
					aria-selected={view === 'html'}
					class="px-3 py-1 rounded-full {view === 'html' ? 'bg-primary-600 text-white' : ''}"
					on:click={() => (view = 'html')}
				>
					As sent
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={view === 'text'}
					class="px-3 py-1 rounded-full {view === 'text' ? 'bg-primary-600 text-white' : ''}"
					on:click={() => (view = 'text')}
				>
					Plain text
				</button>
			</div>

			{#if view === 'html'}
				{#if detail.bodyHTML.trim() === ''}
					<EmptyState compact title="This message has no HTML body." />
				{:else}
					<iframe
						title="Body of {detail.subject}"
						sandbox=""
						srcdoc={detail.bodyHTML}
						class="w-full h-[60vh] rounded-2xl bg-white border-widget"
					/>
				{/if}
			{:else}
				<pre class="whitespace-pre-wrap text-sm bg-depth rounded-2xl p-3 max-h-[60vh] overflow-y-auto">{detail.bodyText}</pre>
			{/if}
		</div>
	{/if}
</Modal>
